import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Save,
  AlertCircle,
} from "lucide-react";
import Button from "../../../components/common/Button/Button";
import PermissionGate from "../../../components/common/PermissionGate/PermissionGate";
import { toast } from "../../../utils/toast";
import {
  usePerformanceCycle,
  useUpdatePerformanceCycle,
} from "../../../hooks/usePerformanceCycles";
// Population controls removed from Edit form as requested

const EditPerformanceCycle = ({ cycleId: propCycleId, onClose, isDrawer }) => {
  const { cycleId: paramCycleId } = useParams();
  const cycleId = propCycleId || paramCycleId;
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    cycleName: "",
    description: "",
    startDate: "",
    endDate: "",
    status: "draft",
    population: {
      organization: "",
      businessUnits: [],
      departments: [],
      locations: [],
      roles: [],
      grades: [],
      selectedEmployeeIds: [],
      eligibilityRules: {
        minTenure: 0,
        maxTenure: null,
        probationThreshold: 6,
        excludeRoles: [],
        includeOnlyActive: true,
      },
      totalEligible: 0,
      totalEnrolled: 0,
    },
    stages: [],
    timeline: {},
    reminders: {},
    visibility: {},
    delegation: {},
    approval: {},
  });

  // React Query hooks
  const {
    data: cycleData,
    isLoading,
    isError,
    error,
  } = usePerformanceCycle(cycleId);

  const updateCycleMutation = useUpdatePerformanceCycle();

  // Population helpers removed

  const statusOptions = [
    { value: "draft", label: "Draft", color: "bg-gray-100 text-gray-800" },
    {
      value: "launched",
      label: "Launched",
      color: "bg-blue-100 text-blue-800",
    },
    { value: "closed", label: "Closed", color: "bg-red-100 text-red-800" },
  ];

  // availableStages removed - not part of create form

  useEffect(() => {
    // The cycle data is directly in cycleData
    if (cycleData && cycleData.id) {
      const cycle = cycleData;
      setFormData({
        cycleName: cycle.cycleName || "",
        description: cycle.description || "",
        startDate: cycle.startDate ? cycle.startDate.split("T")[0] : "",
        endDate: cycle.endDate ? cycle.endDate.split("T")[0] : "",
        status: cycle.status || "draft",
        population: cycle.population || {
          organization: "",
          businessUnits: [],
          departments: [],
          locations: [],
          roles: [],
          grades: [],
          selectedEmployeeIds: [],
          eligibilityRules: {
            minTenure: 0,
            maxTenure: null,
            probationThreshold: 6,
            excludeRoles: [],
            includeOnlyActive: true,
          },
          totalEligible: 0,
          totalEnrolled: 0,
        },
        // Keep stages as objects, not convert to strings
        stages: cycle.stages || [],
        timeline: cycle.timeline || {},
        reminders: cycle.reminders || {},
        visibility: cycle.visibility || {},
        delegation: cycle.delegation || {},
        approval: cycle.approval || {},
      });
    }
  }, [cycleData]);

  // Handle key press for text inputs (alphanumeric, spaces, commas only)
  const handleTextKeyPress = (e) => {
    if (!/^[a-zA-Z0-9\s,]$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
      e.preventDefault();
    }
  };

  const handleInputChange = (field, value) => {
    let processedValue = value;

    // Text fields: only allow alphanumeric, spaces, and commas
    if (field === 'cycleName') {
      processedValue = value.replace(/[^a-zA-Z0-9\s,]/g, '');
      // Enforce maxLength
      if (processedValue.length > 100) processedValue = processedValue.slice(0, 100);
    } else if (field === 'description') {
      processedValue = value.replace(/[^a-zA-Z0-9\s,]/g, '');
      // Enforce maxLength
      if (processedValue.length > 500) processedValue = processedValue.slice(0, 500);
    }

    setFormData((prev) => ({
      ...prev,
      [field]: processedValue || value,
    }));
  };

  // Population change handler removed
  // Stage-related functions removed - not part of create form

  const validateForm = () => {
    const errors = {};

    if (!formData.cycleName.trim()) {
      errors.cycleName = "Cycle name is required";
    } else if (formData.cycleName.length > 100) {
      errors.cycleName = "Cycle name must be ≤ 100 characters";
    } else if (!/^[a-zA-Z0-9\s,]+$/.test(formData.cycleName)) {
      errors.cycleName = "Cycle name can only contain alphanumeric characters, spaces, and commas";
    }

    if (!formData.description.trim()) {
      errors.description = "Description is required";
    } else if (formData.description.length > 500) {
      errors.description = "Description must be ≤ 500 characters";
    } else if (!/^[a-zA-Z0-9\s,]+$/.test(formData.description)) {
      errors.description = "Description can only contain alphanumeric characters, spaces, and commas";
    }

    if (!formData.startDate) {
      errors.startDate = "Start date is required";
    }

    if (!formData.endDate) {
      errors.endDate = "End date is required";
    }

    if (
      formData.startDate &&
      formData.endDate &&
      new Date(formData.startDate) >= new Date(formData.endDate)
    ) {
      errors.endDate = "End date must be after start date";
    }

    // Start date must not be in the past (compare date-only)
    if (formData.startDate) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const start = new Date(formData.startDate);
      if (start < today) {
        errors.startDate = "Start date cannot be in the past";
      }
    }

    // Stages validation removed - not part of create form

    return errors;
  };

  const handleSave = async () => {
    const errors = validateForm();

    if (Object.keys(errors).length > 0) {
      Object.values(errors).forEach((error) => {
        toast.error(error);
      });
      return;
    }

    try {
      // Keep existing stages from the cycle data as objects (not strings)
      const cycleDataToSend = {
        cycleName: formData.cycleName,
        description: formData.description,
        startDate: formData.startDate,
        endDate: formData.endDate,
        status: formData.status,
        // Send stages as objects (backend expects array of stage objects, not strings)
        stages: Array.isArray(formData.stages) ? formData.stages : [],
      };

      await updateCycleMutation.mutateAsync(cycleId, cycleDataToSend);

      toast.success("Performance cycle updated successfully");
      if (isDrawer) {
        onClose?.();
      } else {
        navigate(`/performance_cycle_detail/${cycleId}`);
      }
    } catch (error) {
      console.error("Error updating cycle:", error);
      const errorMessage =
        error.response?.data?.responseMessage ||
        error.message ||
        "Failed to update performance cycle";
      toast.error(errorMessage);
    }
  };

  const handleCancel = () => {
    if (isDrawer) {
      onClose?.();
    } else {
      navigate(`/performance_cycle_detail/${cycleId}`);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand mx-auto mb-4"></div>
          <p className="text-gray-600">Loading cycle data...</p>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-600 mb-4">Error loading cycle data</p>
          <Button onClick={() => navigate("/performance_cycles")}>
            Back to Cycles
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <Button
          variant="outline"
          icon={<ArrowLeft className="h-4 w-4" />}
          onClick={handleCancel}
        >
          Back
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Edit Performance Cycle
          </h1>
          <p className="text-gray-600">Modify the performance cycle details</p>
        </div>
      </div>

      {/* Form */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="space-y-6">
          {/* Basic Information */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Basic Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Cycle Name *
                </label>
                <input
                  type="text"
                  value={formData.cycleName}
                  onChange={(e) =>
                    handleInputChange("cycleName", e.target.value)
                  }
                  onKeyPress={handleTextKeyPress}
                  maxLength={100}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none outline-none shadow-none focus:shadow-none focus-visible:shadow-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
                  placeholder="Enter cycle name"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {formData.cycleName.length}/100 characters
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status *
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => handleInputChange("status", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none outline-none shadow-none focus:shadow-none focus-visible:shadow-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
                >
                  {statusOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Start Date *
                </label>
                <input
                  type="date"
                  value={formData.startDate}
                  onChange={(e) =>
                    handleInputChange("startDate", e.target.value)
                  }
                  min={(new Date()).toISOString().split('T')[0]}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none outline-none shadow-none focus:shadow-none focus-visible:shadow-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  End Date *
                </label>
                <input
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => handleInputChange("endDate", e.target.value)}
                  min={formData.startDate || (new Date()).toISOString().split('T')[0]}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none outline-none shadow-none focus:shadow-none focus-visible:shadow-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description *
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) =>
                    handleInputChange("description", e.target.value)
                  }
                  onKeyPress={handleTextKeyPress}
                  maxLength={1500}
                  required
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none outline-none shadow-none focus:shadow-none focus-visible:shadow-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
                  placeholder="Enter cycle description"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {formData.description.length}/1500 characters
                </p>
              </div>
            </div>
          </div>

          {/* Stages section removed - only show fields that are in create form */}
          {/* Population (beta) removed as requested */}
        </div>
      </div>

      {/* Form Actions */}
      <div className="flex items-center justify-end space-x-4">
        <Button
          type="button"
          variant="outline"
          onClick={handleCancel}
          disabled={updateCycleMutation.isPending}
        >
          Cancel
        </Button>
        <PermissionGate permission="performance" action="update">
          <Button
            type="button"
            variant="primary"
            icon={<Save className="h-4 w-4" />}
            onClick={handleSave}
            disabled={updateCycleMutation.isPending}
            className="bg-brand hover:bg-brand-dark"
          >
            {updateCycleMutation.isPending ? "Saving..." : "Save Changes"}
          </Button>
        </PermissionGate>
      </div>
    </div>
  );
};

export default EditPerformanceCycle;
